import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { NetworkHelper } from '../helpers';
import { SHARED_ENV } from '../tokens';

/**
 * Admin push notifications (Tools → Push notifications).
 *
 * A user can receive one when they have a registered device and have not
 * muted notifications. `en` copy is required; `ru` / `hy` fall back to it.
 * Each language may have its own image, with the same fallback to `en`.
 *
 * A send can also carry an in-app modal (title, description, banner, optional
 * action button). The push then has `data.hasModal === 'true'`, and the app
 * fetches the modal from `GET /push-notifications/:campaignId/modal` on tap.
 * Sending is asynchronous: `send` returns a queued campaign, and the backend
 * worker updates its counters as it goes. Backend: docs/push-notifications-tool.md.
 */

export type PushCampaignStatus =
  | 'queued'
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'failed';

export interface PushLocalizedText {
  en: string;
  ru: string;
  hy: string;
}

/** S3 keys (or URLs) per language; `null` = none, falls back to `en`. */
export interface PushLocalizedImages {
  en: string | null;
  ru: string | null;
  hy: string | null;
}

/** Subset of the Users-page filters; `'any'` means unset. */
export interface PushAudienceFilters {
  search?: string;
  status?: string;
  kycStatus?: string;
  isBlocked?: string;
  termsAccepted?: string;
  role?: string;
  riskLevel?: string;
  onlyNegativeBalance?: boolean;
  lang?: string;
}

export type PushAudience =
  | { type: 'all' }
  | { type: 'filter'; filters: PushAudienceFilters }
  | { type: 'user'; userId: string };

export interface PushStats {
  pushEnabledUsers: number;
  /** Users with at least one device of that platform. */
  android: number;
  ios: number;
  tokens: number;
  totalClients: number;
  /** False when push is switched off in the backend config — sends are refused. */
  pushConfigured: boolean;
}

export interface PushAudienceCount {
  users: number;
  tokens: number;
}

/** The in-app modal opened when the push is tapped. */
export interface PushModal {
  title: PushLocalizedText;
  description: PushLocalizedText;
  /** Banner per language; S3 keys when sending. */
  images: PushLocalizedImages;
  /** Both null when the modal has no action button. */
  buttonText: PushLocalizedText | null;
  /** Per language, e.g. `…?lang=hy`; missing ones fall back to `en`. */
  buttonUrl: PushLocalizedText | null;
}

export interface SendPushPayload {
  title: PushLocalizedText;
  body: PushLocalizedText;
  images?: PushLocalizedImages;
  modal?: PushModal | null;
  audience: PushAudience;
}

export interface PushCampaign {
  id: string;
  status: PushCampaignStatus;
  title: PushLocalizedText;
  body: PushLocalizedText;
  images: PushLocalizedImages;
  imageUrls: PushLocalizedImages;
  modal: (PushModal & { imageUrls: PushLocalizedImages }) | null;
  audience: {
    type: 'all' | 'filter' | 'user';
    filters: PushAudienceFilters | null;
    userId: string | null;
  };
  createdBy: string;
  createdByEmail?: string | null;
  stats: {
    targetUsers: number;
    targetTokens: number;
    sent: number;
    failed: number;
    invalidTokensRemoved: number;
  };
  startedAt: string | null;
  finishedAt: string | null;
  error: string | null;
  created: string;
  updated: string;
}

@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  private readonly env = inject(SHARED_ENV);
  private readonly networkHelper = inject(NetworkHelper);
  private readonly hostUrl: string;

  constructor() {
    this.hostUrl = this.env.host;
  }

  getStats(): Observable<PushStats> {
    return this.networkHelper.get(
      `${this.hostUrl}/admin/push-notifications/stats`
    );
  }

  /** Push-enabled users (and their devices) the audience would reach. */
  getAudienceCount(audience: PushAudience): Observable<PushAudienceCount> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/push-notifications/audience-count`,
      { audience }
    );
  }

  /** Irreversible — pushes cannot be recalled once delivered. */
  send(payload: SendPushPayload): Observable<PushCampaign> {
    return this.networkHelper.post(
      `${this.hostUrl}/admin/push-notifications/send`,
      payload
    );
  }

  getCampaigns(
    page: number,
    itemsPerPage: number
  ): Observable<{ items: PushCampaign[]; total: number }> {
    return this.networkHelper.get(
      `${this.hostUrl}/admin/push-notifications/campaigns`,
      { page, itemsPerPage }
    );
  }

  getCampaign(id: string): Observable<PushCampaign> {
    return this.networkHelper.get(
      `${this.hostUrl}/admin/push-notifications/campaigns/${id}`
    );
  }
}
